import { useState } from 'react';
import { Modal, th } from '../../components/common';
import { IconCamera, IconSave } from '../../components/icons';
import { Switch } from './shell';

/**
 * หมอนวด — built from tools/captures/dialog-therapist.json.
 *
 * One dialog serves both jobs, the way theirs does: a name in the list opens
 * the same sheet the สร้างหมอนวด button opens, already filled in. The two
 * FormModals this replaces had drifted apart — the create one wrote fields the
 * edit one could not reach.
 */

const GENDERS = [
  { value: 'female', label: 'หญิง' },
  { value: 'male', label: 'ชาย' },
  { value: 'other', label: 'อื่น ๆ' },
  { value: 'unspecified', label: 'ไม่ระบุ' },
];

/* The three the capture lists. The fourth grouping their list shows,
   หมอนวดพักงานหรือเลิกจ้าง, is the สถานะ switch below being off. */
const EMPLOYMENT_TYPES = ['full_time', 'daily', 'part_time'];

/* ISO weekday numbers, so Monday is 1 — their chips run จ. to อา. rather than
   starting the week on Sunday. */
const DAYS = [
  { value: 1, label: 'จ.' }, { value: 2, label: 'อ.' }, { value: 3, label: 'พ.' },
  { value: 4, label: 'พฤ.' }, { value: 5, label: 'ศ.' }, { value: 6, label: 'ส.' },
  { value: 7, label: 'อา.' },
];

/* _note: only +66 (TH) was captured. The rest are the countries the shop's own
   staff carry numbers from, so a Burmese or Lao phone is not filed under a Thai
   dial code — their full list is not known. */
const DIAL_CODES = [
  { value: '+66', label: '+66 (TH)' },
  { value: '+95', label: '+95 (MM)' },
  { value: '+856', label: '+856 (LA)' },
  { value: '+855', label: '+855 (KH)' },
  { value: '+86', label: '+86 (CN)' },
  { value: '+44', label: '+44 (GB)' },
  { value: '+1', label: '+1 (US)' },
];

// The API accepts 2 MB JSON bodies. Keeping the source image to 1 MB leaves
// enough room for base64 expansion plus the rest of the therapist payload.
const MAX_QR_IMAGE_BYTES = 1024 * 1024;
const MAX_PROFILE_SOURCE_BYTES = 12 * 1024 * 1024;
const MAX_PROFILE_STORED_BYTES = 450 * 1024;

const imageDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('ไม่สามารถอ่านไฟล์รูปภาพได้'));
  reader.readAsDataURL(file);
});

// Phone photos are commonly several megabytes. Resize them before putting the
// data URL in PostgreSQL so the therapist payload remains below the API's 2 MB
// JSON limit even when the same therapist also has a PromptPay QR image.
const profileImageDataUrl = async (file) => {
  if (file.size <= MAX_PROFILE_STORED_BYTES) return imageDataUrl(file);

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('ไม่สามารถเปิดไฟล์รูปหมอนวดได้'));
      element.src = objectUrl;
    });

    const maxSide = 900;
    const scale = Math.min(1, maxSide / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext('2d');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    let quality = 0.82;
    let result = canvas.toDataURL('image/jpeg', quality);
    while (result.length > 600000 && quality > 0.5) {
      quality -= 0.08;
      result = canvas.toDataURL('image/jpeg', quality);
    }
    return result;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

/* The list, the board, ค่ามือ and the booking builder all render a therapist as
   `ชื่อ (ชื่อไทย)`. Their dialog carries one ชื่อเล่น box, so the box shows
   exactly that string and saving reads it back the same way — a row opened and
   saved untouched comes out of the list character for character. */
const nicknameOf = (t) =>
  [t?.nickname_en, t?.nickname_th && `(${t.nickname_th})`].filter(Boolean).join(' ');

const splitNickname = (text) => {
  const parts = text.trim().match(/^(.*?)\s*\((.+)\)$/);
  return parts
    ? { en: parts[1] || null, th: parts[2] }
    : { en: text.trim() || null, th: null };
};

const Text = ({ label, value, onChange, ...rest }) => (
  <div className="field">
    <label>{label}</label>
    <input value={value} onChange={(e) => onChange(e.target.value)} {...rest} />
  </div>
);

const Select = ({ label, value, options, onChange }) => (
  <div className="field">
    <label>{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

/**
 * @param therapist  the row being edited, or null when creating
 * @param services   the shop's services, for the ประเภทบริการเฉพาะ restriction
 */
export default function TherapistDialog({ therapist, services, onSubmit, onClose }) {
  const [v, setV] = useState(() => ({
    code: therapist?.code || '',
    nickname: nicknameOf(therapist),
    firstName: therapist?.first_name || '',
    lastName: therapist?.last_name || '',
    email: therapist?.email || '',
    phoneCountryCode: therapist?.phone_country_code || '+66',
    phone: therapist?.phone || '',
    gender: therapist?.gender || 'unspecified',
    employmentType: therapist?.employment_type || 'full_time',
    photoUrl: therapist?.photo_url || '',
    promptPayQrUrl: therapist?.promptpay_qr_url || '',
    workingDays: (therapist?.working_days || DAYS.map((d) => d.value)).map(Number),
    serviceIds: (therapist?.service_ids || []).map(Number),
    isActive: therapist ? therapist.is_active !== false : true,
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setV((s) => ({ ...s, [key]: value }));

  const toggleIn = (key, value) => set(
    key,
    v[key].includes(value) ? v[key].filter((x) => x !== value) : [...v[key], value],
  );

  const choosePhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('กรุณาเลือกไฟล์รูปภาพของหมอนวด');
      return;
    }
    if (file.size > MAX_PROFILE_SOURCE_BYTES) {
      setError('รูปหมอนวดต้องมีขนาดไม่เกิน 12 MB');
      return;
    }

    try {
      set('photoUrl', await profileImageDataUrl(file));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const choosePromptPayQr = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('กรุณาเลือกไฟล์รูปภาพสำหรับ QR พร้อมเพย์');
      return;
    }
    if (file.size > MAX_QR_IMAGE_BYTES) {
      setError('รูป QR พร้อมเพย์ต้องมีขนาดไม่เกิน 1 MB');
      return;
    }

    try {
      set('promptPayQrUrl', await imageDataUrl(file));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const submit = async () => {
    if (!v.nickname.trim()) {
      setError('กรุณากรอกชื่อเล่น');
      return;
    }
    if (!v.code.trim()) {
      setError('กรุณากรอกรหัสหมอนวด');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const name = splitNickname(v.nickname);
      await onSubmit({
        code: v.code.trim(),
        nicknameEn: name.en,
        nicknameTh: name.th,
        firstName: v.firstName || null,
        lastName: v.lastName || null,
        email: v.email || null,
        phone: v.phone || null,
        phoneCountryCode: v.phoneCountryCode,
        gender: v.gender,
        employmentType: v.employmentType,
        photoUrl: v.photoUrl || null,
        promptPayQrUrl: v.promptPayQrUrl || null,
        // Sorted so the chips and the stored row read in the same order.
        workingDays: [...v.workingDays].sort((a, b) => a - b),
        serviceIds: v.serviceIds,
        isActive: v.isActive,
      });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={therapist ? 'แก้ไขหมอนวด' : 'สร้างหมอนวด'}
      className="staff-modal"
      onClose={onClose}
      footer={(
        <>
          <button className="btn-outline" onClick={onClose} disabled={busy}>ยกเลิก</button>
          <button className="btn-solid" onClick={submit} disabled={busy}>
            <IconSave size={16} /> {busy ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </>
      )}
    >
      {error && <div className="login-error">{error}</div>}

      <div className="svc-section">
        <div className="svc-top">
          <div className="svc-fields">
            <label className="svc-photo settings-photo-upload" title="อัปโหลดรูปหมอนวด">
              {v.photoUrl ? <img src={v.photoUrl} alt="" /> : null}
              <span className="badge-cam"><IconCamera size={14} /></span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                aria-label="อัปโหลดรูปหมอนวด"
                onChange={choosePhoto}
              />
            </label>
            <span className="hint settings-photo-hint">กดที่รูปเพื่ออัปโหลด · ไม่เกิน 12 MB</span>
            {v.photoUrl && (
              <button
                type="button"
                className="btn-outline settings-photo-remove"
                onClick={() => set('photoUrl', '')}
              >
                ลบรูป
              </button>
            )}
          </div>

          <div className="svc-fields">
            <div className="field">
              <div className="field-count">
                <label>ชื่อเล่น <span style={{ color: 'var(--red-600)' }}>*</span></label>
                {/* Their counter reads /10, but names already in the shop's list
                    run past that — พี่อุ้ม เสือป่า is 14 — so the count is shown
                    and not enforced. A cap here would lock those rows shut. */}
                <span className="counter">{v.nickname.length}/10</span>
              </div>
              <input value={v.nickname} onChange={(e) => set('nickname', e.target.value)} />
            </div>

            {/* _note: their dialog has no รหัสหมอนวด. Ours needs one — the code
                is the therapist's unique key and every punch and payout hangs
                off it — so the field stays rather than being invented at save. */}
            <Text
              label={<>รหัสหมอนวด <span style={{ color: 'var(--red-600)' }}>*</span></>}
              value={v.code}
              onChange={(x) => set('code', x)}
            />

            <div className="grid-2">
              <Text label="ชื่อจริง" value={v.firstName} onChange={(x) => set('firstName', x)} />
              <Text label="นามสกุล" value={v.lastName} onChange={(x) => set('lastName', x)} />
            </div>

            <div className="grid-2">
              <Text label="อีเมล" value={v.email} onChange={(x) => set('email', x)} />
              <div className="field">
                <label>เบอร์โทรศัพท์</label>
                <div className="phone-field">
                  <select
                    value={v.phoneCountryCode}
                    onChange={(e) => set('phoneCountryCode', e.target.value)}
                  >
                    {DIAL_CODES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  <input
                    value={v.phone}
                    placeholder="0xxxxxxxxx"
                    onChange={(e) => set('phone', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <Select
              label="เพศของหมอนวด"
              value={v.gender}
              options={GENDERS}
              onChange={(x) => set('gender', x)}
            />
            <Select
              label="ประเภทหมอนวด"
              value={v.employmentType}
              options={EMPLOYMENT_TYPES.map((t) => ({ value: t, label: th(t) }))}
              onChange={(x) => set('employmentType', x)}
            />
          </div>
        </div>

        <div className="field">
          <label>วันทำงาน</label>
          <div className="multiselect">
            {DAYS.map((d) => (
              <button
                type="button"
                key={d.value}
                className={`staff-chip${v.workingDays.includes(d.value) ? '' : ' off'}`}
                onClick={() => toggleIn('workingDays', d.value)}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <label>ประเภทบริการเฉพาะที่หมอนวดสามารถทำได้</label>
          {/* Theirs is a single select whose options were not captured. Ours
              lists the services themselves because that is what the restriction
              is stored as — therapist_skill, the other half of a service's
              ใช้กับทั้งหมด switch. Picking none leaves the therapist bookable
              for everything that is not restricted, which is their "ทั้งหมด". */}
          {services.length ? (
            <div className="multiselect">
              {services.map((s) => (
                <button
                  type="button"
                  key={s.id}
                  className={`staff-chip small${v.serviceIds.includes(s.id) ? '' : ' off'}`}
                  onClick={() => toggleIn('serviceIds', s.id)}
                >
                  {[s.name_en, s.name_th].filter(Boolean).join(' ')}
                </button>
              ))}
            </div>
          ) : (
            <span className="hint">ยังไม่มีบริการ — เพิ่มได้ที่ ตั้งค่า → บริการ</span>
          )}
        </div>
      </div>

      <div className="svc-section promptpay-section">
        <h3>QR พร้อมเพย์สำหรับจ่ายค่ามือ</h3>
        <span className="hint">
          QR นี้ผูกกับหมอนวดทุกสาขา และจะแสดงในหน้าค่ามือเมื่อเลือกหมอนวดคนนี้
        </span>
        <div className="promptpay-upload">
          <div className="promptpay-preview">
            {v.promptPayQrUrl
              ? <img src={v.promptPayQrUrl} alt="QR พร้อมเพย์" />
              : <span>ยังไม่มี QR พร้อมเพย์</span>}
          </div>
          <div className="promptpay-upload-actions">
            <label className="btn-outline promptpay-file">
              <IconCamera size={16} />
              {v.promptPayQrUrl ? 'เปลี่ยนรูป QR' : 'อัปโหลดรูป QR'}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={choosePromptPayQr}
              />
            </label>
            {v.promptPayQrUrl && (
              <button
                type="button"
                className="btn-outline"
                onClick={() => set('promptPayQrUrl', '')}
              >
                ลบรูป QR
              </button>
            )}
            <span className="hint">PNG, JPG หรือ WEBP · ไม่เกิน 1 MB</span>
          </div>
        </div>
      </div>

      <div className="svc-section">
        <Switch
          label="สถานะ"
          hint="ไม่ใช้งาน / ใช้งาน"
          on={v.isActive}
          onChange={(x) => set('isActive', x)}
        />
      </div>
    </Modal>
  );
}
